import { useSyncExternalStore } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AUState, HomeLocation as ApiHomeLocation, User } from '@superp/shared';
import { api, getAccessToken } from './api';
import { useCurrentUser } from './useCurrentUser';

/**
 * Where the user lives.
 *
 * Two stores, one hook. Signed out, this is localStorage — someone browsing
 * anonymously still deserves local alerts, and there is nowhere else to put
 * it. Signed in, the account is the truth, because a suburb set on the laptop
 * has to be there on the phone.
 *
 * The interesting case is the seam: someone sets their suburb while signed
 * out, then signs in. Their choice must not evaporate, so it is pushed up to
 * the account on first sight — see `useSyncLocalLocationToAccount`. The local
 * copy stays as the offline read model, and the account overwrites it whenever
 * the two disagree, so the account always wins a genuine conflict.
 */

const STORAGE_KEY = 'burro_location';
const DISMISS_KEY = 'burro_location_prompt_dismissed';
const EVENT_NAME = 'burro:location-changed';

export interface HomeLocation {
  state: AUState;
  suburb: string | null;
  lat: number | null;
  lng: number | null;
  source: 'manual' | 'geolocation';
  setAt: string;
}

// AU state rough bounding boxes (lat min/max, lng min/max). Used to derive
// a state code from browser geolocation without a network round-trip, purely
// so the UI can render something immediately. The server re-derives the state
// from the same coordinates with a far more careful classifier before storing
// it, so a border-town miss here is corrected within one request rather than
// persisted.
const STATE_BBOXES: Array<[AUState, number, number, number, number]> = [
  ['ACT', -35.95, -35.1, 148.7, 149.4],
  ['NSW', -37.6, -28.15, 140.99, 153.8],
  ['VIC', -39.3, -33.95, 140.95, 150.05],
  ['QLD', -29.2, -9.2, 137.99, 153.6],
  ['SA', -38.1, -25.99, 128.99, 141.05],
  ['WA', -35.2, -13.5, 112.9, 129.05],
  ['TAS', -43.75, -39.5, 143.8, 148.5],
  ['NT', -26.05, -10.95, 128.99, 138.05],
];

export function stateFromCoords(lat: number, lng: number): AUState | null {
  // ACT first so it's not swallowed by NSW
  for (const [code, latMin, latMax, lngMin, lngMax] of STATE_BBOXES) {
    if (lat >= latMin && lat <= latMax && lng >= lngMin && lng <= lngMax) {
      return code;
    }
  }
  return null;
}

/**
 * Cached against the raw string, for the reason spelled out in local-first.ts:
 * `useSyncExternalStore` compares snapshots by identity, so parsing afresh on
 * every call hands React a new object each time and it re-renders forever.
 *
 * This was latent rather than theoretical — it only bites once somebody has
 * actually SET a location, because before that the snapshot is null and null
 * is identical to null.
 */
let cachedRaw: string | null = null;
let cachedValue: HomeLocation | null = null;

function readStorage(): HomeLocation | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  if (raw === cachedRaw) return cachedValue;
  cachedRaw = raw;
  try {
    cachedValue = JSON.parse(raw) as HomeLocation;
  } catch {
    cachedValue = null;
  }
  return cachedValue;
}

function writeStorage(loc: HomeLocation | null): void {
  try {
    if (loc) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    window.dispatchEvent(new Event(EVENT_NAME));
  } catch {
    /* ignore */
  }
}

export function setHomeLocation(loc: Omit<HomeLocation, 'setAt'>): void {
  writeStorage({ ...loc, setAt: new Date().toISOString() });
}

export function clearHomeLocation(): void {
  writeStorage(null);
}

export function isLocationPromptDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissLocationPrompt(): void {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
    window.dispatchEvent(new Event(EVENT_NAME));
  } catch {
    /* ignore */
  }
}

function subscribe(callback: () => void): () => void {
  const handler = () => callback();
  window.addEventListener(EVENT_NAME, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(EVENT_NAME, handler);
    window.removeEventListener('storage', handler);
  };
}

/** The local copy only. Prefer `useHomeLocation` unless you specifically want this. */
export function useLocalHomeLocation(): HomeLocation | null {
  return useSyncExternalStore(subscribe, readStorage, () => null);
}

/** Translate the account shape into the shape the UI already speaks. */
export function fromAccount(account: Pick<
  User,
  'homeState' | 'homeSuburb' | 'homeLat' | 'homeLng' | 'homeSetAt'
> | null | undefined): HomeLocation | null {
  if (!account?.homeState) return null;
  return {
    state: account.homeState,
    suburb: account.homeSuburb,
    lat: account.homeLat,
    lng: account.homeLng,
    source: 'manual',
    setAt: account.homeSetAt ?? new Date(0).toISOString(),
  };
}

/**
 * Where this person lives — account first, local copy second.
 *
 * Falls back to the local copy while `/auth/me` is still in flight so the
 * hero does not flash "set your suburb" at someone who set it months ago.
 */
export function useHomeLocation(): HomeLocation | null {
  const local = useLocalHomeLocation();
  const { data: user } = useCurrentUser();
  const signedIn = !!getAccessToken();
  if (!signedIn) return local;
  return fromAccount(user) ?? local;
}

/**
 * The three ways someone can tell us where they live, mirroring the API:
 * a suburb they picked, coordinates from the browser, or a bare state.
 */
export type SetLocationInput =
  | { placeId: string; sessionToken?: string; label?: string }
  | { lat: number; lng: number; state: AUState }
  | { state: AUState };

/**
 * Read a state out of a suggestion label — "Castlemaine VIC 3450, Australia".
 *
 * Only used on the signed-out path, where there is no server to resolve the
 * placeId and the label is genuinely all we have. Signed in, the state is
 * always derived from coordinates instead, because a label is whatever Google
 * printed and the border towns are exactly where it misleads.
 */
export function stateFromLabel(label: string | undefined): AUState | null {
  if (!label) return null;
  const match = /\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/.exec(label.toUpperCase());
  return (match?.[1] as AUState | undefined) ?? null;
}

/** Write a location to whichever store is authoritative for this person. */
export function useSetHomeLocation() {
  const queryClient = useQueryClient();
  const signedIn = !!getAccessToken();

  return useMutation({
    mutationFn: async (input: SetLocationInput) => {
      if (signedIn) {
        const body =
          'placeId' in input
            ? { placeId: input.placeId, sessionToken: input.sessionToken, label: input.label }
            : 'lat' in input
              ? { lat: input.lat, lng: input.lng }
              : { state: input.state };
        return api<ApiHomeLocation>('/location/home', {
          method: 'PUT',
          body: JSON.stringify(body),
        });
      }
      // Signed out: the local copy is all there is. A placeId cannot be
      // resolved to coordinates without the server, so a suburb pick degrades
      // to the state we can read off its label and is resolved properly the
      // moment they sign in.
      const state = 'state' in input ? input.state : stateFromLabel(input.label);
      if (!state) {
        throw new Error('Sign in to save that suburb, or pick a state instead');
      }
      setHomeLocation({
        state,
        suburb: 'label' in input ? (input.label ?? null) : null,
        lat: 'lat' in input ? input.lat : null,
        lng: 'lng' in input ? input.lng : null,
        source: 'lat' in input ? 'geolocation' : 'manual',
      });
      return null;
    },
    onSuccess: (result) => {
      if (result) {
        // Mirror down so the offline read model agrees, and so a signed-in
        // user who later signs out keeps the suburb they chose.
        if (result.state) {
          setHomeLocation({
            state: result.state,
            suburb: result.suburb,
            lat: result.lat,
            lng: result.lng,
            source: 'manual',
          });
        }
        void queryClient.invalidateQueries({ queryKey: ['me'] });
      }
      void queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

/** Forget it, in both stores. */
export function useClearHomeLocation() {
  const queryClient = useQueryClient();
  const signedIn = !!getAccessToken();

  return useMutation({
    mutationFn: async () => {
      if (signedIn) await api<ApiHomeLocation>('/location/home', { method: 'DELETE' });
      clearHomeLocation();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      void queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

/**
 * Carry a locally-chosen suburb up to the account on sign-in.
 *
 * Only ever fills a gap: if the account already has a location it wins, and
 * the local copy is corrected to match. Runs once per mount, guarded on
 * having coordinates — a local record with no lat/lng (an older one, or a
 * manual state pick) cannot be resolved server-side, so it stays local rather
 * than being written up as a half-truth.
 */
export function useSyncLocalLocationToAccount(): void {
  const local = useLocalHomeLocation();
  const { data: user } = useCurrentUser();
  const signedIn = !!getAccessToken();
  const queryClient = useQueryClient();

  const accountHas = !!user?.homeState;
  const canPush = signedIn && !!user && !accountHas && !!local?.lat && !!local?.lng;

  useQuery({
    queryKey: ['location-sync', user?.id, local?.setAt],
    enabled: canPush,
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      await api<ApiHomeLocation>('/location/home', {
        method: 'PUT',
        body: JSON.stringify({ lat: local!.lat, lng: local!.lng }),
      });
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      await queryClient.invalidateQueries({ queryKey: ['alerts'] });
      return true;
    },
  });
}
